<template>
  <div
    class="asc-fleet-open-maint-wrapper"
    data-automation-id="wrapper_fleet_open_maint"
  >
    <template v-if="loading">
      <asc-spinner-wrapper loading></asc-spinner-wrapper>
    </template>
    <template v-if="!loading">
      <div class="asc-fleet-open-maint--content">
        <asc-filter-legend
          :items="getLegendItems"
          :items-selected="legendItemsSelected"
          :on-item-click="handleLegendItemClick"
          tooltip="Select any status to filter below"
        ></asc-filter-legend>
        <div class="asc-fleet-open-maint--chart-content">
          <asc-open-maintenance-bar-chart
            :values="filteredTailCountsData"
            :series-name="chartSeriesName"
            :category-name="chartCategoryName"
            :loading="loading"
            :selected-item="getSelectedTail"
            :is-issue-investigator="isIssueInvestigator"
            :is-issue-reader="isIssueReader"
            :is-issue-updater="isIssueUpdater"
            :maintenance-statuses="maintenanceStatuses"
            @onSeriesClick="handleTailSelected"
          ></asc-open-maintenance-bar-chart>
        </div>
        <template v-if="isTailNumberSelected">
          <div class="asc-fleet-open-maint--grid-content">
            <asc-header
              uppercase
              size="h4"
              :value="`Open Maintenance Recommendation Count for ${selectedTail}`"
            />
            <asc-open-maintenance-grid
              :values="filteredOpenMaintenanceData"
              :grid-columns="openMaintenanceGridColumns"
              :grid-filters="false"
            ></asc-open-maintenance-grid>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import AscHeader from '../atoms/AscHeader.vue'
import AscSpinnerWrapper from '../atoms/AscSpinnerWrapper.vue'

import AscFilterLegend from '../molecules/AscFilterLegend.vue'
import AscOpenMaintenanceBarChart from '../molecules/AscOpenMaintenanceBarChart.vue'
import AscOpenMaintenanceGrid from '../molecules/AscOpenMaintenanceGrid.vue'

import { COLLINS_PHM_TIER_4, COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'

import _orderBy from 'lodash/orderBy'
import _sumBy from 'lodash/sumBy'
import _groupBy from 'lodash/groupBy'

export default {
  name: 'AscFleetOpenMaintWrapper',

  components: {
    AscFilterLegend,
    AscHeader,
    AscOpenMaintenanceBarChart,
    AscOpenMaintenanceGrid,
    AscSpinnerWrapper,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    fleetData: {
      type: Array,
      default: () => [],
    },
    openMaintenanceData: {
      type: Array,
      default: () => [],
    },
    openMaintenanceGridColumns: {
      type: Array,
      default: () => [],
    },
    onOpenMaintenance: {
      type: Function,
      default: () => {},
    },
    onOpenFde: {
      type: Function,
      default: () => {},
    },
    onOpenConnectivity: {
      type: Function,
      default: () => {},
    },
    maintenanceStatuses: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      chartSeriesName: 'Count',
      chartCategoryName: 'Tail',
      filteredTailCountsData: [],
      filteredFleetData: null,
      filteredOpenMaintenanceData: [],
      selectedTail: '',
      tailNumber: null,
      legendItemsSelected: this.getLegendItemsSelectedFromLocalStorage(),
    }
  },

  computed: {
    isTailNumberSelected() {
      return this.selectedTail !== null
    },

    getSelectedTail() {
      return this.selectedTail
    },

    getLegendItems() {
      const nonTier4Statuses = this.maintenanceStatuses.filter(
        status => status.isExternal,
      )

      const hasTier4Airlines =
        this.fleetData
          .map(tier => tier.airlineTierTypeId)
          .includes(COLLINS_PHM_TIER_4) ||
        this.openMaintenanceData
          .map(tier => tier.airlineTierTypeId)
          .includes(COLLINS_PHM_TIER_4)

      if (this.isIssueUpdater) {
        return [
          {
            header: '',
            column: 12,
            items: this.maintenanceStatuses,
          },
        ]
      }

      if (
        (this.isIssueReader || this.isIssueInvestigator) &&
        hasTier4Airlines
      ) {
        return [
          {
            header: 'Airline',
            column: 8,
            borderRight: true,
            items: this.maintenanceStatuses.filter(status => status.isExternal),
          },
          {
            header: 'Fleet Monitor',
            multipleColumns: false,
            column: 4,
            paddingLeft: true,
            items: this.maintenanceStatuses.filter(
              status => !status.isExternal,
            ),
          },
        ]
      }

      return [
        {
          header: 'Airline',
          column: 8,
          borderRight: false,
          items: nonTier4Statuses.filter(item =>
            item.agreementTypeIds.includes(COLLINS_PHM_TIER_3),
          ),
        },
      ]
    },
  },

  watch: {
    fleetData(newVal) {
      this.fleetData = newVal
      this.updateData()
    },

    openMaintenanceData(newVal) {
      this.openMaintenanceData = newVal
      this.updateData()
    },
  },

  mounted() {
    this.updateData()
  },

  methods: {
    isValidStatusName(status) {
      if (this.isIssueUpdater) {
        return this.maintenanceStatuses
          .map(status => status.name)
          .includes(status.statusName)
      }

      return true
    },

    getLegendItemsSelectedFromLocalStorage() {
      let items = null
      if (this.isIssueUpdater) {
        items = localStorage.getItem('asc_open_maint_legend_items_ext')
      }

      if (!this.isIssueUpdater) {
        items = localStorage.getItem('asc_open_maint_legend_items_int')
      }

      return items ? JSON.parse(items) : []
    },

    setLegendItemsSelectedToLocalStorage() {
      if (this.isIssueUpdater) {
        localStorage.setItem(
          'asc_open_maint_legend_items_ext',
          JSON.stringify(this.legendItemsSelected),
        )
      }

      if (!this.isIssueUpdater) {
        localStorage.setItem(
          'asc_open_maint_legend_items_int',
          JSON.stringify(this.legendItemsSelected),
        )
      }
    },

    isStatusNameSelected(status) {
      return this.legendItemsSelected.includes(status)
    },

    handleLegendItemClick(status) {
      this.selectedTail = null
      const isFound = this.legendItemsSelected.includes(status)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== status,
        )
      } else {
        this.legendItemsSelected.push(status)
      }

      this.updateData()
      this.setLegendItemsSelectedToLocalStorage()
    },

    handleTailSelected(tailNumber) {
      this.selectedTail = tailNumber
      this.filteredOpenMaintenanceData = this.getFilteredOpenMaintenanceData()
      this.filteredFleetData = this.getFilteredFleetData()
    },

    getTailCounts() {
      // init
      const groups = ['tailNumber', 'statusName']
      const grouped = {}

      // group openMaintenanceData by tailNumber and statusName
      this.openMaintenanceData
        .filter(item => this.isValidStatusName(item))
        .forEach(a => {
          groups
            .reduce((o, g, i) => {
              o[a[g]] = o[a[g]] || (i + 1 === groups.length ? [] : {})
              return o[a[g]]
            }, grouped)
            .push(a)
        })

      const tailsWithLastAlertDate = Object.values(
        this.openMaintenanceData.reduce((op, { tailNumber, lastAlertDate }) => {
          op[tailNumber] = op[tailNumber] || {
            tailNumber,
            lastAlertDate: -Infinity,
          }
          op[tailNumber].lastAlertDate =
            op[tailNumber].lastAlertDate < new Date(lastAlertDate).getTime()
              ? new Date(lastAlertDate).getTime()
              : op[tailNumber].lastAlertDate
          return op
        }, {}),
      )

      // count issues for each tail
      const tails = Object.keys(grouped).map((key1, index1) => {
        const statusesData = Object.values(grouped)[index1]
        const statuses = Object.keys(statusesData).map((key2, index2) => {
          const statusData = Object.values(statusesData)[index2]
          return {
            statusName: key2,
            issueCount: statusData.length,
          }
        })
        return {
          tailNumber: key1,
          statuses,
        }
      })

      // transform tail data
      const issueCounts = tails.map(tail => {
        const groupedByStatuses = _groupBy(tail.statuses, 'statusName')

        const statusByIssueCount = Object.keys(groupedByStatuses).map(
          (item, index) => {
            return {
              statusName: item,
              issueCount: !this.isStatusNameSelected(item)
                ? _sumBy(Object.values(groupedByStatuses)[index], 'issueCount')
                : 0,
            }
          },
        )

        const [filteredTail] = tailsWithLastAlertDate.filter(
          tail => tail.tailNumber === tail.tailNumber,
        )

        return {
          name: tail.tailNumber,
          lastAlertDate: filteredTail ? filteredTail.lastAlertDate : 0,
          statusByIssueCount: statusByIssueCount,
          total: _sumBy(statusByIssueCount, 'issueCount'),
        }
      })

      // sort tails and filter tails
      const filteredSortedTails = _orderBy(
        issueCounts,
        ['total', 'lastAlertDate'],
        ['desc', 'desc'],
      ).filter(item => item.total > 0)

      // return filteredSortedTails
      return filteredSortedTails
    },

    getFilteredTailCountData() {
      const tailData = this.getTailCounts()
      if (tailData.length) {
        const data = tailData
        this.selectedTail = data[0].name

        return data
      }

      this.selectedTail = ''
      return []
    },

    getFilteredFleetData() {
      const [tail] = this.fleetData.filter(
        item => item.name === this.selectedTail,
      )
      return tail || null
    },

    getFilteredOpenMaintenanceData() {
      return this.openMaintenanceData
        .filter(item => this.isValidStatusName(item))
        .filter(item => item.tailNumber === this.selectedTail)
    },

    updateData() {
      this.filteredTailCountsData = this.getFilteredTailCountData()
      this.filteredOpenMaintenanceData = this.getFilteredOpenMaintenanceData()
      this.filteredFleetData = this.getFilteredFleetData()
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-open-maint-wrapper {
  padding: 16px;
  background-color: $primary-white;

  .asc-fleet-open-maint--chart-content {
    margin: 16px 0;
    padding: 8px;
    border: 1px solid $gray-300;
  }

  .asc-fleet-open-maint--grid-content {
    margin: 16px 0;
    padding: 16px;
    border: 1px solid $gray-300;

    .flex {
      display: flex;
    }

    .width100 {
      width: 100%;
    }

    .asc-header--h4 {
      font-size: 14px !important;
      font-weight: 500 !important;
      line-height: 18px !important;
      letter-spacing: 1px !important;
      margin-bottom: 16px;
    }
  }
}
</style>
